import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import ItsmIcon from '../components/ItsmIcon';
import { getProductivity } from '../services/api';
import {
  TrendingUp, CheckCircle, AlertTriangle, Star, BookOpen,
  Timer, RefreshCw, User, Search, Ticket, Zap, ChevronRight, Activity,
  Trophy, Clock, Target, Sparkles,
} from 'lucide-react';

const PERIOD_OPTIONS = [
  { label: '7 days',  value: 7  },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const GRADS = [
  'linear-gradient(135deg,#ff6aa9,#ff8f6b)',
  'linear-gradient(135deg,#7c6cf0,#a78bfa)',
  'linear-gradient(135deg,#22d3ee,#2dd4bf)',
  'linear-gradient(135deg,#fbbf24,#f59e0b)',
  'linear-gradient(135deg,#34d399,#10b981)',
  'linear-gradient(135deg,#60a5fa,#3b82f6)',
];

function scoreConsultant(c) {
  let s = 0;
  s += Math.min(c.resolved_period * 5, 40);
  s += c.avg_rating ? parseFloat(c.avg_rating) * 6 : 0;
  s += c.response_compliance ? parseFloat(c.response_compliance) * 0.2 : 0;
  s -= c.sla_breached * 3;
  s += parseFloat(c.checkin_hours) * 0.5;
  return Math.max(0, Math.round(s));
}

/* gamified tier */
function gamerTier(score) {
  if (score >= 80) return { label: 'MASTER',  emoji: '🏆', color: '#f59e0b', grad: 'linear-gradient(135deg,#fbbf24,#f59e0b)' };
  if (score >= 50) return { label: 'PRO',      emoji: '⭐', color: '#3b82f6', grad: 'linear-gradient(135deg,#60a5fa,#3b82f6)' };
  if (score >= 25) return { label: 'RISING',   emoji: '🚀', color: '#7c6cf0', grad: 'linear-gradient(135deg,#a78bfa,#7c6cf0)' };
  return              { label: 'ROOKIE',   emoji: '🌱', color: '#10b981', grad: 'linear-gradient(135deg,#34d399,#10b981)' };
}

const initials = (n) => (n || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

function Avatar({ name, size = 40, ring }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg,#7c6cf0,#a78bfa)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 800,
      border: ring ? `3px solid ${ring}` : 'none', boxShadow: '0 4px 12px rgba(124,108,240,0.35)',
    }}>{initials(name)}</div>
  );
}

/* radar / spider chart */
function Radar({ axes }) {
  const N = axes.length, R = 66, cx = 110, cy = 100;
  const pt = (i, frac) => {
    const a = -Math.PI / 2 + (i / N) * 2 * Math.PI;
    return [cx + frac * R * Math.cos(a), cy + frac * R * Math.sin(a)];
  };
  const rings = [0.25, 0.5, 0.75, 1];
  const poly = (frac, mapVal) => axes.map((ax, i) => pt(i, mapVal ? ax.value : frac).join(',')).join(' ');
  return (
    <svg viewBox="0 0 220 200" style={{ width: '100%', maxWidth: 230 }}>
      {rings.map((r, i) => (
        <polygon key={i} points={poly(r, false)} fill="none" stroke="var(--border)" strokeWidth="1" />
      ))}
      {axes.map((ax, i) => {
        const [x, y] = pt(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border)" strokeWidth="1" />;
      })}
      <polygon points={poly(0, true)} fill="rgba(124,108,240,0.28)" stroke="#7c6cf0" strokeWidth="2.5" strokeLinejoin="round" />
      {axes.map((ax, i) => {
        const [x, y] = pt(i, ax.value);
        return <circle key={i} cx={x} cy={y} r="3.5" fill="#7c6cf0" />;
      })}
      {axes.map((ax, i) => {
        const [x, y] = pt(i, 1.26);
        return <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
          fontSize="9.5" fontWeight="700" fill="var(--text-muted)">{ax.label}</text>;
      })}
    </svg>
  );
}

function GradStat({ grad, icon: Icon, label, value, sub }) {
  return (
    <div style={{ background: grad, borderRadius: 20, padding: '16px 16px 14px', color: '#fff', boxShadow: '0 10px 24px rgba(0,0,0,0.12)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.18)' }} />
      <div style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, position: 'relative', zIndex: 1 }}>
        <Icon size={19} color="#fff" />
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, position: 'relative', zIndex: 1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11.5, fontWeight: 600, opacity: 0.92, marginTop: 4, position: 'relative', zIndex: 1 }}>{label}</div>
      {sub && <div style={{ fontSize: 10.5, opacity: 0.8, marginTop: 1, position: 'relative', zIndex: 1 }}>{sub}</div>}
    </div>
  );
}

function ComplianceRow({ label, value, color }) {
  const pct = parseFloat(value) || 0;
  const c = color || (pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444');
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-sub)', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: value !== null ? c : 'var(--text-muted)' }}>{value !== null ? `${pct}%` : 'No data'}</span>
      </div>
      <div style={{ height: 9, background: 'var(--gray-100)', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 8, background: c, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
}

export default function ProductivityPage() {
  const navigate  = useNavigate();
  const [days,     setDays]     = useState(30);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]   = useState('');
  const [apiError, setApiError] = useState(null);

  const load = async () => {
    setLoading(true); setApiError(null);
    try {
      const res = await getProductivity({ days });
      setData(res.data);
      // default-select the TOP performer (highest score), not just the first in the list
      if (!selected && res.data.consultants?.length) {
        const top = [...res.data.consultants].sort((a, b) => scoreConsultant(b) - scoreConsultant(a))[0];
        setSelected(top.id);
      }
    } catch (err) {
      setApiError(err?.response?.data?.error || err?.message || 'Failed to load productivity data');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  const consultants = (data?.consultants || []).map(c => ({ ...c, score: scoreConsultant(c) }));
  const ranked = [...consultants].sort((a, b) => b.score - a.score);
  const maxResolved = Math.max(...consultants.map(c => c.resolved_period), 1);
  const consultant = consultants.find(c => c.id === selected);
  const railList = ranked.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()));

  const totals = consultants.reduce((a, c) => ({
    assigned: a.assigned + c.assigned_period, resolved: a.resolved + c.resolved_period,
    breached: a.breached + c.sla_breached, kb: a.kb + c.kb_articles,
    hours: a.hours + parseFloat(c.checkin_hours), pending: a.pending + c.pending_total,
  }), { assigned: 0, resolved: 0, breached: 0, kb: 0, hours: 0, pending: 0 });

  const tier = consultant ? gamerTier(consultant.score) : null;
  // tie-aware (competition) rank: everyone with the same score shares a rank
  const rank = consultant ? consultants.filter(c => c.score > consultant.score).length + 1 : 0;
  const tiedCount = consultant ? consultants.filter(c => c.score === consultant.score).length : 0;

  const radarAxes = consultant ? [
    { label: 'Resolve',  value: Math.min((parseFloat(consultant.resolution_rate) || 0) / 100, 1) },
    { label: 'Response', value: Math.min((parseFloat(consultant.response_compliance) || 0) / 100, 1) },
    { label: 'Rating',   value: Math.min((parseFloat(consultant.avg_rating) || 0) / 5, 1) },
    { label: 'Volume',   value: Math.min(consultant.resolved_period / maxResolved, 1) },
    { label: 'Reliable', value: 1 - Math.min(consultant.sla_breached / Math.max(consultant.resolved_period + consultant.sla_breached, 5), 1) },
  ] : [];

  const podium = [ranked[1], ranked[0], ranked[2]].filter(Boolean);
  const podiumMeta = { 0: { h: 70, badge: '🥈', ring: '#cbd5e1' }, 1: { h: 96, badge: '🥇', ring: '#fbbf24' }, 2: { h: 56, badge: '🥉', ring: '#f0a868' } };

  return (
    <AppLayout pageTitle="Consultant Productivity">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes floaty { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .prod-grid { display:grid; grid-template-columns: 1fr 300px; gap:18px; align-items:start; }
        .ov-row { display:grid; grid-template-columns: 1.1fr 1fr 1.1fr; gap:16px; }
        .stat-row { display:grid; grid-template-columns: repeat(6,1fr); gap:14px; }
        .metric-grid { display:grid; grid-template-columns: repeat(3,1fr); gap:12px; }
        .pcard { background: var(--bg-card); border:1px solid var(--border); border-radius:22px; box-shadow:0 6px 22px rgba(15,23,42,0.06); }
        .rail-item:hover { background: var(--bg-hover, rgba(0,0,0,0.03)); }
        .lb-item:hover { background: var(--bg-hover, rgba(0,0,0,0.03)); }
        @media (max-width:1100px){ .prod-grid{grid-template-columns:1fr} .ov-row{grid-template-columns:1fr} .stat-row{grid-template-columns:repeat(3,1fr)} }
        @media (max-width:640px){ .stat-row{grid-template-columns:repeat(2,1fr)} .metric-grid{grid-template-columns:repeat(2,1fr)} }
      `}</style>

      {/* ── Hero banner ── */}
      <div style={{
        position: 'relative', overflow: 'hidden', borderRadius: 26, padding: '26px 30px', marginBottom: 20,
        background: 'linear-gradient(120deg,#7c6cf0 0%,#9b5cf0 42%,#ff6aa9 100%)',
        boxShadow: '0 16px 40px rgba(124,108,240,0.32)',
      }}>
        <div style={{ position: 'relative', zIndex: 2, maxWidth: '64%' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.2)', padding: '5px 12px', borderRadius: 20, fontSize: 11.5, fontWeight: 700, color: '#fff', marginBottom: 12 }}>
            <Sparkles size={13} /> Team Performance Arena
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', lineHeight: 1.15, margin: 0 }}>
            Resolve, Rank Up & Win! 🎮
          </h2>
          <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.9)', marginTop: 8, lineHeight: 1.5 }}>
            Track each consultant's score, climb the leaderboard, and celebrate top performers.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {PERIOD_OPTIONS.map(o => (
              <button key={o.value} onClick={() => setDays(o.value)} style={{
                padding: '8px 16px', borderRadius: 22, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: 'none', fontFamily: 'inherit',
                background: days === o.value ? '#fff' : 'rgba(255,255,255,0.2)',
                color: days === o.value ? '#7c3aed' : '#fff', transition: 'all 0.15s',
              }}>{o.label}</button>
            ))}
            <button onClick={load} title="Refresh" style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <RefreshCw size={15} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
            </button>
          </div>
        </div>
        <div style={{ position: 'absolute', right: 24, bottom: -6, zIndex: 1, animation: 'floaty 5s ease-in-out infinite' }}>
          <ItsmIcon size={210} idSuffix="-prod" style={{ filter: 'drop-shadow(0 14px 28px rgba(0,0,0,0.3))' }} />
        </div>
        <div style={{ position: 'absolute', top: 90, right: 200, fontSize: 30, animation: 'floaty 4s ease-in-out infinite' }}>🏆</div>
        <div style={{ position: 'absolute', top: 30, right: 120, fontSize: 22, animation: 'floaty 6s ease-in-out infinite' }}>⭐</div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-muted)' }}>
          <RefreshCw size={28} style={{ animation: 'spin 0.8s linear infinite', marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>Loading…</p>
        </div>
      ) : apiError ? (
        <div className="pcard" style={{ textAlign: 'center', padding: 60 }}>
          <AlertTriangle size={40} color="#dc2626" style={{ marginBottom: 14 }} />
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#dc2626' }}>Failed to load</p>
          <p style={{ margin: '6px 0 20px', fontSize: 13, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{apiError}</p>
          <button onClick={load} style={{ padding: '8px 20px', borderRadius: 20, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Retry</button>
        </div>
      ) : consultants.length === 0 ? (
        <div className="pcard" style={{ textAlign: 'center', padding: 60 }}>
          <User size={40} style={{ opacity: 0.25, marginBottom: 14 }} />
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-muted)' }}>No consultants found</p>
          <button onClick={() => navigate('/users')} style={{ marginTop: 16, padding: '8px 20px', borderRadius: 20, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Go to User Management</button>
        </div>
      ) : (
        <div className="prod-grid">
          {/* ════ MAIN ════ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>

            {/* Overview: rank card · tier trophy · radar */}
            {consultant && (
              <div className="ov-row">
                {/* rank / level card */}
                <div className="pcard" style={{ padding: '18px 18px 16px', textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', position: 'relative', marginBottom: 8 }}>
                    <Avatar name={consultant.name} size={68} ring={tier.color} />
                    <span style={{ position: 'absolute', bottom: -4, right: -6, fontSize: 18 }}>{tier.emoji}</span>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{consultant.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>Lv {consultant.score} · Rank #{rank}{tiedCount > 1 ? ` (tied ×${tiedCount})` : ''}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>
                    <span>Score</span><span style={{ fontWeight: 700, color: tier.color }}>{consultant.score}/100</span>
                  </div>
                  <div style={{ height: 9, background: 'var(--gray-100)', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(consultant.score, 100)}%`, borderRadius: 8, background: tier.grad }} />
                  </div>
                </div>

                {/* tier trophy card */}
                <div className="pcard" style={{ padding: '18px', textAlign: 'center', background: tier.grad, color: '#fff', border: 'none', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ fontSize: 46, lineHeight: 1, animation: 'floaty 4s ease-in-out infinite' }}>{tier.emoji}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '1px', marginTop: 6 }}>{tier.label}</div>
                  <div style={{ fontSize: 11.5, opacity: 0.9, marginTop: 2 }}>{consultant.resolved_period} resolved · {days}d</div>
                  <button onClick={() => navigate(`/tickets?assigned_to=${consultant.id}`)} style={{ marginTop: 12, padding: '7px 18px', borderRadius: 20, border: 'none', background: '#fff', color: tier.color, fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                    View Tickets
                  </button>
                </div>

                {/* radar */}
                <div className="pcard" style={{ padding: '14px 10px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-main)', marginBottom: 2 }}>Performance</div>
                  <Radar axes={radarAxes} />
                </div>
              </div>
            )}

            {/* Team stat tiles (colorful) */}
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-main)', margin: '0 0 12px' }}>⚡ Team Stats</h3>
              <div className="stat-row">
                <GradStat grad={GRADS[4]} icon={CheckCircle}   label={`Resolved (${days}d)`} value={totals.resolved} />
                <GradStat grad={GRADS[1]} icon={Ticket}        label={`Assigned (${days}d)`} value={totals.assigned} />
                <GradStat grad={GRADS[2]} icon={Activity}      label="Pending" value={totals.pending} />
                <GradStat grad={GRADS[0]} icon={AlertTriangle} label="SLA Breaches" value={totals.breached} />
                <GradStat grad={GRADS[3]} icon={Timer}         label={`Check-in (${days}d)`} value={`${totals.hours.toFixed(0)}h`} />
                <GradStat grad={GRADS[5]} icon={BookOpen}      label={`KB Articles`} value={totals.kb} />
              </div>
            </div>

            {/* Leaderboard podium */}
            <div className="pcard" style={{ padding: '20px 22px' }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-main)', margin: '0 0 18px', display: 'flex', alignItems: 'center', gap: 7 }}>
                <Trophy size={17} color="#f59e0b" /> Leaderboard
              </h3>
              {podium.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 18, marginBottom: 8 }}>
                  {podium.map((c, i) => {
                    const m = podiumMeta[i];
                    return (
                      <div key={c.id} onClick={() => setSelected(c.id)} style={{ textAlign: 'center', cursor: 'pointer', flex: 1, maxWidth: 130 }}>
                        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 6 }}>
                          <Avatar name={c.name} size={i === 1 ? 56 : 46} ring={m.ring} />
                          <span style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', fontSize: 18 }}>{m.badge}</span>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name?.split(' ')[0]}</div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: gamerTier(c.score).color }}>{c.score} pts</div>
                        <div style={{ height: m.h, marginTop: 6, borderRadius: '10px 10px 0 0', background: gamerTier(c.score).grad, opacity: 0.9, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 6, color: '#fff', fontWeight: 800, fontSize: 14 }}>
                          {i === 1 ? '1' : i === 0 ? '2' : '3'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* rest of ranking */}
              {ranked.slice(3).length > 0 && (
                <div style={{ marginTop: 14, borderTop: '1px solid var(--border-light)', paddingTop: 6 }}>
                  {ranked.slice(3).map((c, i) => (
                    <div key={c.id} onClick={() => setSelected(c.id)} className="lb-item" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 8px', borderRadius: 10, cursor: 'pointer' }}>
                      <span style={{ width: 22, textAlign: 'center', fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' }}>{i + 4}</span>
                      <Avatar name={c.name} size={30} />
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: gamerTier(c.score).color }}>{c.score} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected consultant detail */}
            {consultant && (
              <>
                <div className="metric-grid">
                  {[
                    { icon: CheckCircle, label: `Resolved (${days}d)`, value: consultant.resolved_period, c: '#10b981' },
                    { icon: Ticket, label: `Assigned (${days}d)`, value: consultant.assigned_period, c: '#7c6cf0' },
                    { icon: Activity, label: 'Pending Now', value: consultant.pending_total, c: '#06b6d4' },
                    { icon: Clock, label: 'Avg Resolution', value: consultant.avg_resolution_hours ? `${consultant.avg_resolution_hours}h` : '—', c: '#3b82f6' },
                    { icon: Zap, label: 'Resolved / Day', value: consultant.resolved_per_day, c: '#10b981' },
                    { icon: AlertTriangle, label: 'SLA Breaches', value: consultant.sla_breached, c: '#ef4444' },
                    { icon: Target, label: `Critical (${days}d)`, value: consultant.critical_handled, c: '#f59e0b' },
                    { icon: BookOpen, label: `KB Articles`, value: consultant.kb_articles, c: '#06b6d4' },
                    { icon: Star, label: 'Avg Rating', value: consultant.avg_rating ? `${parseFloat(consultant.avg_rating).toFixed(1)}★` : '—', c: '#f59e0b' },
                  ].map(m => (
                    <div key={m.label} className="pcard" style={{ padding: '14px 16px', display: 'flex', gap: 12, alignItems: 'center', borderRadius: 16 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: m.c + '1f', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <m.icon size={19} color={m.c} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-main)', lineHeight: 1.1 }}>{m.value ?? '—'}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{m.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pcard" style={{ padding: '18px 22px' }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 800, color: 'var(--text-main)' }}>🎯 Performance Quests</h4>
                  <ComplianceRow label="First Response SLA Compliance" value={consultant.response_compliance} />
                  <ComplianceRow label={`Resolution Rate (${days}d)`} value={consultant.resolution_rate} />
                  <ComplianceRow label="Customer Satisfaction" value={consultant.avg_rating ? (parseFloat(consultant.avg_rating) * 20).toFixed(0) : null} color="#f59e0b" />
                </div>
              </>
            )}
          </div>

          {/* ════ RIGHT RAIL: consultant list ════ */}
          <div className="pcard" style={{ padding: '16px 14px' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-main)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
              <User size={16} color="var(--primary)" /> Consultants
            </div>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                style={{ width: '100%', padding: '9px 12px 9px 32px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-main)', fontSize: 12.5, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 560, overflowY: 'auto' }}>
              {railList.map(c => {
                const t = gamerTier(c.score);
                const active = selected === c.id;
                return (
                  <div key={c.id} onClick={() => setSelected(c.id)} className="rail-item" style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 9px', borderRadius: 12, cursor: 'pointer',
                    background: active ? 'var(--primary-light)' : 'transparent', border: `1px solid ${active ? 'var(--primary)' : 'transparent'}`,
                  }}>
                    <Avatar name={c.name} size={34} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: active ? 'var(--primary)' : 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{c.resolved_period} resolved · {c.score} pts</div>
                    </div>
                    <span style={{ fontSize: 9.5, fontWeight: 800, color: '#fff', background: t.grad, padding: '3px 8px', borderRadius: 20, flexShrink: 0 }}>{t.emoji} {t.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
